import React, { useState } from 'react';
import {
  Alert, Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, InputAdornment, Stack, TextField, Typography,
} from '@mui/material';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { fetchUnpricedCompletedAppointments, setAppointmentFinalPrice } from '../../../firebase/firebase-delegate';
import { formatDate } from '../../../time/timeUtils';
import { Appointment } from '../../../types';
import LoadingSpinner from '../../../components/global/LoadingSpinner';

type UnpricedBookingsDialogProps = {
  open: boolean;
  onClose: () => void;
  tenantId: string;
  // Who is entering prices (saved as lastUpdater).
  actorId: string;
  // Limit to one staff member, or undefined for the whole business.
  employeeId?: string;
};

const byDate = (left: Appointment, right: Appointment) => {
  const key = (appointment: Appointment) =>
    `${appointment.appointmentDate.slice(4)}${appointment.appointmentDate.slice(0, 4)}${appointment.appointmentTime}`;
  return key(left).localeCompare(key(right));
};

const PriceRow: React.FC<{ appointment: Appointment; onSave: (price: number) => Promise<void> }> = ({ appointment, onSave }) => {
  const [text, setText] = useState('');
  const [saving, setSaving] = useState(false);
  const value = Number(text);
  const valid = text.trim() !== '' && Number.isFinite(value) && value >= 0;
  return (
    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} alignItems={{ sm: 'center' }}
      sx={{ py: 1.5, borderTop: '1px solid', borderColor: 'divider' }}>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography fontWeight={800}>{appointment.customerFirstName} {appointment.customerLastName}</Typography>
        <Typography variant="body2" color="text.secondary">
          {formatDate(appointment.appointmentDate)} · {appointment.appointmentTitle} · {appointment.employeeName}
        </Typography>
      </Box>
      <Stack direction="row" spacing={1} alignItems="center">
        <TextField size="small" label="Final price" value={text} disabled={saving} inputMode="decimal" sx={{ width: 140 }}
          onChange={(event) => {
            const next = event.target.value.replace(/[^\d.]/g, '');
            if (/^\d*(\.\d{0,2})?$/.test(next)) setText(next);
          }}
          InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment> }} />
        <Button variant="contained" disabled={!valid || saving} onClick={async () => {
          setSaving(true);
          try { await onSave(value); } finally { setSaving(false); }
        }}>
          {saving ? 'Saving…' : 'Save'}
        </Button>
      </Stack>
    </Stack>
  );
};

// Completed "Price varies" bookings that still need a final price. Saving one
// updates revenue and the customer's total through the stats triggers.
const UnpricedBookingsDialog: React.FC<UnpricedBookingsDialogProps> = ({ open, onClose, tenantId, actorId, employeeId }) => {
  const queryClient = useQueryClient();
  const listKey = ['unpriced-bookings', tenantId, employeeId ?? 'all'];
  const listQuery = useQuery({
    queryKey: listKey,
    enabled: open,
    queryFn: () => fetchUnpricedCompletedAppointments(tenantId, employeeId),
  });
  const saveMutation = useMutation({
    mutationFn: ({ appointment, price }: { appointment: Appointment; price: number }) =>
      setAppointmentFinalPrice(appointment, price, actorId, tenantId),
    onSuccess: (_result, { appointment }) => {
      queryClient.setQueryData<Appointment[]>(listKey, (current) =>
        (current ?? []).filter((entry) => entry.id !== appointment.id));
      // Stats update on the server a moment later; refresh the charts then.
      window.setTimeout(() => void queryClient.invalidateQueries({ queryKey: ['analytics', tenantId] }), 1500);
    },
  });
  const bookings = [...(listQuery.data ?? [])].sort(byDate);

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle sx={{ fontWeight: 900 }}>Bookings that need a final price</DialogTitle>
      <DialogContent>
        <Typography color="text.secondary" sx={{ mb: 1 }}>
          These completed bookings include a “Price varies” service. Enter what each one cost so revenue is complete.
        </Typography>
        {listQuery.isLoading ? <LoadingSpinner inline label="Loading bookings" /> : null}
        {listQuery.isError ? <Alert severity="error">The bookings couldn’t be loaded. Try again.</Alert> : null}
        {saveMutation.isError ? <Alert severity="error" sx={{ mb: 1 }}>That price couldn’t be saved. Try again.</Alert> : null}
        {!listQuery.isLoading && !listQuery.isError && bookings.length === 0 ? (
          <Alert severity="success">All caught up. Every completed booking has a price.</Alert>
        ) : null}
        {bookings.map((appointment) => (
          <PriceRow key={appointment.id} appointment={appointment}
            onSave={(price) => saveMutation.mutateAsync({ appointment, price }).then(() => undefined)} />
        ))}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose}>Done</Button>
      </DialogActions>
    </Dialog>
  );
};

export default UnpricedBookingsDialog;
